import ReceivableReminderSettings from "@/components/ReceivableReminderSettings";
import WriteButton from "@/components/WriteButton";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import ReportActions from '@/components/ReportActions';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MoneyInput from "@/components/MoneyInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { HandCoins, Search, History, Wallet, CheckCircle2, Printer } from "lucide-react";
import { printReceivablePayment } from '@/lib/printReceivablePayment';

const METHODS = [
  { key: "tunai", label: "Tunai" },
  { key: "transfer", label: "Transfer" },
  { key: "qris", label: "QRIS" },
];

export default function Piutang() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [payFor, setPayFor] = useState(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("tunai");
  const [saving, setSaving] = useState(false);
  const [histFor, setHistFor] = useState(null);
  const [hist, setHist] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [includePaid, setIncludePaid] = useState(false);
  const paymentAttempt = useRef(null);
  const paying = useRef(false);

  const load = async () => {
    try {
      const { data } = await api.get("/piutang", {params: {include_paid: includePaid}});
      setList(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  useEffect(() => { load(); }, [includePaid]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(
    () => list.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()) || (c.phone || "").includes(search)),
    [list, search]
  );
  const totalDebt = useMemo(() => list.reduce((s, c) => s + (c.debt || 0), 0), [list]);

  const openPay = (c) => { paymentAttempt.current = null; setPayFor(c); setAmount(String(c.debt || "")); setMethod("tunai"); };
  const doPay = async () => {
    if (paying.current) return;
    const amt = Number(amount || 0);
    if (amt <= 0) return toast.error("Jumlah pembayaran tidak valid");
    const signature = JSON.stringify([payFor.id, amt, method]);
    if (paymentAttempt.current && paymentAttempt.current.signature !== signature) return toast.error('Periksa riwayat atau ulangi nominal yang sama; pembayaran sebelumnya belum terkonfirmasi.');
    if (!paymentAttempt.current) paymentAttempt.current = {signature, id: crypto.randomUUID()};
    paying.current = true;
    setSaving(true);
    try {
      const {data} = await api.post(`/piutang/${payFor.id}/pay`, { amount: amt, method, client_request_id: paymentAttempt.current.id });
      toast.success(`Pembayaran dari ${payFor.name} tercatat`);
      setReceipt({payment: data.payment_receipt, reprint: false});
      paymentAttempt.current = null;
      setPayFor(null);
      load();
    } catch (e) {
      if (e.response && e.response.status < 500) paymentAttempt.current = null;
      toast.error(apiError(e));
    } finally {
      paying.current = false;
      setSaving(false);
    }
  };

  const openHist = async (c) => {
    setHistFor(c);
    setHist(null);
    try {
      const { data } = await api.get(`/piutang/${c.id}/history`);
      setHist(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const fmtDate = (v) => (v ? new Date(v).toLocaleString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");

  return (
    <>
      <PageHeader title="Piutang" subtitle="Daftar pelanggan berhutang & pelunasan">
        <div className="ml-auto flex w-full flex-wrap items-center justify-between gap-2 sm:w-auto sm:justify-end">
        <div className="flex min-w-0 items-center gap-2 rounded-lg bg-danger-soft border border-danger/30 px-2.5 py-2 sm:px-4">
          <Wallet className="h-4 w-4 text-danger-readable" />
          <span className="text-[11px] sm:text-xs text-ui-muted">Total</span>
          <span data-testid="total-debt" className="min-w-0 break-all text-xs sm:text-sm font-mono font-bold text-danger-readable">{rupiah(totalDebt)}</span>
        </div>
        <ReceivableReminderSettings />
        </div>
      </PageHeader>

      <div className="flex-1 min-w-0 overflow-y-auto p-3 sm:p-5">
        <ReportActions title="Daftar Piutang Pelanggan" headers={['Nama Pelanggan','Telepon','Sisa Piutang']} rows={filtered.map(c => [c.name,c.phone,c.debt])} notes={[`Pencarian: ${search || 'Semua'}`, 'Saldo piutang saat ekspor; bukan bukti pelunasan.']} />
        <label className="mb-3 flex items-center gap-2 text-xs text-ui-muted"><input data-testid="piutang-include-paid" type="checkbox" checked={includePaid} onChange={e => setIncludePaid(e.target.checked)} />Tampilkan juga pelanggan lunas untuk cetak ulang</label>
        <div className="relative mb-3 w-full sm:mb-4 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-muted" />
          <Input
            data-testid="piutang-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama / no. HP…"
            className="pl-10 bg-ui-card border-ui-line"
          />
        </div>

        <div className="hidden md:block rounded-xl border border-ui-line overflow-hidden">
          <table className="w-full text-sm" data-testid="piutang-table">
            <thead className="bg-ui-card text-ui-muted text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left p-3">Pelanggan</th>
                <th className="text-left p-3 hidden md:table-cell">No. HP</th>
                <th className="text-right p-3">Sisa Hutang</th>
                <th className="text-right p-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`piutang-row-${c.id}`} className="border-t border-ui-line hover:bg-ui-soft">
                  <td className="p-3 font-medium">{c.name}</td>
                  <td className="p-3 hidden md:table-cell text-ui-muted font-mono">{c.phone || "-"}</td>
                  <td className="p-3 text-right font-mono font-bold text-danger-readable">{rupiah(c.debt)}</td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <WriteButton data-testid={`pay-btn-${c.id}`} size="sm" onClick={() => openPay(c)} className="h-8 bg-brand-button hover:bg-brand-button text-ui-text font-bold gap-1 text-xs">
                        <HandCoins className="h-3.5 w-3.5" /> Bayar
                      </WriteButton>
                      <Button data-testid={`hist-btn-${c.id}`} size="sm" variant="outline" onClick={() => openHist(c)} className="h-8 border-ui-line bg-transparent hover:bg-ui-card gap-1 text-xs">
                        <History className="h-3.5 w-3.5" /> Riwayat
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={4} className="p-10 text-center text-ui-muted">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-brand-readable/60" /> Tidak ada piutang. Semua lunas!
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="space-y-2 md:hidden" data-testid="piutang-mobile-list">
          {filtered.map((c) => (
            <article key={c.id} data-testid={`piutang-mobile-row-${c.id}`} className="min-w-0 rounded-xl border border-ui-line bg-ui-card p-3">
              <div className="flex min-w-0 items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <h2 className="break-words text-sm font-semibold">{c.name}</h2>
                  {c.phone && <p className="mt-0.5 break-all text-xs text-ui-muted">{c.phone}</p>}
                </div>
                <div className="max-w-[48%] break-words text-right font-mono text-sm font-bold text-danger-readable">{rupiah(c.debt)}</div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <WriteButton data-testid={`pay-btn-${c.id}`} size="sm" onClick={() => openPay(c)} className="h-9 min-w-0 bg-brand-button px-2 text-ui-text font-bold gap-1 text-xs">
                  <HandCoins className="h-3.5 w-3.5 shrink-0" /> Bayar
                </WriteButton>
                <Button data-testid={`hist-btn-${c.id}`} size="sm" variant="outline" onClick={() => openHist(c)} className="h-9 min-w-0 border-ui-line bg-transparent px-2 gap-1 text-xs">
                  <History className="h-3.5 w-3.5 shrink-0" /> Riwayat
                </Button>
              </div>
            </article>
          ))}
          {filtered.length === 0 && <div className="rounded-xl border border-ui-line bg-ui-card p-6 text-center text-sm text-ui-muted"><CheckCircle2 className="mx-auto mb-2 h-7 w-7 text-brand-readable/60" />Tidak ada piutang. Semua lunas!</div>}
        </div>
      </div>

      {/* Pay dialog */}
      <Dialog open={!!payFor} onOpenChange={(v) => !v && !saving && setPayFor(null)}>
        <DialogContent data-testid="pay-dialog" className="w-[calc(100%-1.5rem)] max-h-[90dvh] overflow-y-auto bg-ui-card border-ui-line text-ui-text max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Pelunasan Piutang</DialogTitle>
            <DialogDescription className="text-ui-muted text-xs">Saldo masuk ke shift kasir yang menerima pembayaran.</DialogDescription>
          </DialogHeader>
          {payFor && (
            <div className="space-y-3">
              <div className="rounded-xl bg-ui-card border border-ui-line p-3 flex items-center justify-between">
                <span className="text-ui-muted text-sm">{payFor.name}</span>
                <span className="font-mono font-bold text-danger-readable">{rupiah(payFor.debt)}</span>
              </div>
              <div>
                <label className="text-xs text-ui-muted uppercase tracking-wide">Jumlah Bayar</label>
                <MoneyInput
                  data-testid="pay-amount-input"
                  value={amount}
                  onValueChange={setAmount}
                  className="h-12 text-right font-mono text-xl bg-ui-card border-ui-line mt-1"
                  autoFocus
                />
                <button data-testid="pay-lunas-btn" onClick={() => setAmount(payFor.debt)} className="text-[11px] text-brand-readable hover:text-brand-readable mt-1 font-semibold">
                  Lunas semua ({rupiah(payFor.debt)})
                </button>
              </div>
              <div>
                <label className="text-xs text-ui-muted uppercase tracking-wide">Metode</label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {METHODS.map((m) => (
                    <button
                      key={m.key}
                      data-testid={`pay-method-${m.key}`}
                      onClick={() => setMethod(m.key)}
                      className={`h-10 rounded-lg border text-xs font-semibold no-select ${method === m.key ? "border-brand bg-brand-soft text-brand-readable" : "border-ui-line bg-ui-soft text-ui-muted"}`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" disabled={saving} onClick={() => setPayFor(null)} className="border-ui-line bg-transparent hover:bg-ui-card">Batal</Button>
            <WriteButton data-testid="confirm-pay-btn" onClick={doPay} disabled={saving} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold gap-2">
              <HandCoins className="h-4 w-4" /> Bayar
            </WriteButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!histFor} onOpenChange={(v) => !v && setHistFor(null)}>
        <DialogContent data-testid="hist-dialog" className="w-[calc(100%-1.5rem)] max-h-[90dvh] overflow-y-auto bg-ui-card border-ui-line text-ui-text max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Riwayat · {histFor?.name}</DialogTitle>
            <DialogDescription className="text-ui-muted text-xs">Transaksi berhutang & pembayaran.</DialogDescription>
          </DialogHeader>
          {!hist ? (
            <div className="py-8 text-center text-ui-muted text-sm">Memuat…</div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <div className="text-xs uppercase tracking-wide text-brand-readable font-semibold mb-1.5">Pembayaran</div>
                {hist.payments.length === 0 ? (
                  <div className="text-xs text-ui-muted">Belum ada pembayaran</div>
                ) : hist.payments.map((p, i) => (
                  <div key={i} data-testid={`hist-pay-${i}`} className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1 text-sm border-t border-ui-line py-2">
                    <span className="min-w-0 flex-1 text-xs text-ui-muted">{fmtDate(p.at)} · {p.by} · <span className="uppercase">{p.method}</span></span>
                    <span className="shrink-0 font-mono text-brand-readable">{rupiah(p.amount)}</span>
                    {p.id && <Button data-testid={`print-pay-${i}`} size="sm" variant="outline" onClick={() => {setHistFor(null); setReceipt({payment: p, reprint: true});}} className="h-8 gap-1 text-xs"><Printer className="h-3.5 w-3.5" />Cetak ulang</Button>}
                    {p.reversed_at && <span className="text-xs text-danger-readable">Dibatalkan</span>}
                  </div>
                ))}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-danger-readable font-semibold mb-1.5">Transaksi Berhutang</div>
                {hist.debts.length === 0 ? (
                  <div className="text-xs text-ui-muted">Tidak ada</div>
                ) : hist.debts.map((d, i) => (
                  <div key={i} data-testid={`hist-debt-${i}`} className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1 text-sm border-t border-ui-line py-2">
                    <span className="min-w-0 flex-1 text-xs text-ui-muted">{fmtDate(d.at)} · {d.invoice_no}</span>
                    <span className="text-right font-mono text-xs text-ui-muted">Total {rupiah(d.total)}<br/><span className="text-danger-readable">Sisa {rupiah(d.credit)}</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!receipt?.payment} onOpenChange={v => !v && setReceipt(null)}>
        <DialogContent data-testid="receivable-receipt-dialog" className="max-w-sm max-h-[90dvh] overflow-y-auto bg-ui-card border-ui-line text-ui-text">
          <DialogHeader><DialogTitle>Bukti Pembayaran Piutang</DialogTitle><DialogDescription>Pembayaran sudah tersimpan. Cetak ulang tidak menambah pembayaran.</DialogDescription></DialogHeader>
          {receipt?.payment && <div className="space-y-3 break-words">
            <p className="font-bold">{receipt.payment.customer_name}</p>
            <p className="text-xs text-ui-muted">{receipt.payment.receipt_no}<br/>{fmtDate(receipt.payment.at)}</p>
            {receipt.payment.reversed_at && <p className="font-bold text-danger-readable">PEMBAYARAN DIBATALKAN</p>}
            <div className="flex justify-between gap-2"><span>Dibayar</span><strong>{rupiah(receipt.payment.amount)}</strong></div>
            <div className="flex justify-between gap-2"><span>Sisa saat pembayaran</span><strong>{receipt.payment.debt_after == null ? '—' : rupiah(receipt.payment.debt_after)}</strong></div>
            <Button data-testid="print-receivable-btn" className="w-full gap-2" onClick={() => printReceivablePayment(receipt.payment, receipt.reprint)}><Printer className="h-4 w-4" />{receipt.reprint ? 'Cetak ulang resi' : 'Cetak resi pembayaran'}</Button>
          </div>}
        </DialogContent>
      </Dialog>
    </>
  );
}
